// The CONNECT half of `/debug`: which providers this HOST holds credentials for, and exactly which key is
// missing when one does not.
//
// It answers a question no product surface can. An unkeyed provider and an unconnected one render the same
// "Not connected" card; the difference only appears as a 502 the moment somebody presses Connect, by which
// point the popup has opened and shut. The connections tab now says "Setup pending" rather than offering that
// dead button — this page is where an operator finds out WHICH key to go and set.
//
// Raw fetch rather than the generated client, matching its `useSyncChainDebug` sibling: these routes are
// band-scoped `[InternalController]` diagnostics reached only from `/debug`, an artist-shell route with no
// roster equivalent, so no org-lens middleware is ever mounted over them (the one thing a raw fetch would
// bypass). The TYPES, though, come from the generated schema rather than being hand-mirrored — the routes do
// appear in `openapi.json`, so there is no reason to keep a second copy of the shape here and let it drift.
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandQuery } from "./useBandQuery.js";
import { ApiError } from "./apiError.js";
import { API_URL } from "../../config/env.js";

/** One credential, and whether this host holds it. `value` is already redacted server-side — a secret reads
 *  `***(set)` or `(null)`, a non-secret (the redirect URI) reads verbatim, which is most of the value here. */
export type ConnectConfigKey = components["schemas"]["ConnectConfigKey"];

/** A registered OAuth connect provider and whether pressing Connect could possibly work. */
export type ConnectProviderRow = components["schemas"]["ConnectProviderRow"];

export type ConnectTraceDoc = components["schemas"]["ConnectTraceDoc"];

const CONNECT_KEY = ["debug", "connect"] as const;

/** The host's connect estate. Config only — no vendor call, no database read, no writes, so it is always
 *  safe to open and cannot itself be the thing that is broken. */
export function useConnectDebug() {
  const { tokenProvider } = useApp();
  return useBandQuery<ConnectTraceDoc>(CONNECT_KEY, async (band) => {
    const bearer = tokenProvider.getBearer();
    const response = await fetch(`${API_URL}/api/v1/bands/${band}/debug/connect`, {
      method: "GET",
      credentials: "include",
      headers: bearer ? { Authorization: `Bearer ${bearer}` } : {},
    });

    if (!response.ok) {
      // A 404 is the EXPECTED shape in production, where FilteredControllerFeatureProvider drops the
      // controller entirely — and on any lane whose EnvName the allowlist does not recognise.
      let problem: unknown;
      try {
        problem = await response.json();
      } catch {
        problem = undefined;
      }
      throw new ApiError(response.status, problem);
    }

    const body = (await response.json()) as { data?: ConnectTraceDoc };
    if (!body?.data) throw new ApiError(response.status, undefined);
    return body.data;
  });
}
